import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

export const MONTHS = ["Ιαν", "Φεβ", "Μαρ", "Απρ", "Μάι", "Ιουν", "Ιουλ", "Αυγ", "Σεπ", "Οκτ", "Νοε", "Δεκ"];
const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const diffCls = (a, b, cost) => ((cost ? a > b : a < b) ? "text-[#B5452F]" : "text-[#6E7A63]");

const Card = ({ id, label, value, sub, cls = "" }) => (
  <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid={`bs-kpi-${id}`}>
    <div className="text-[10px] uppercase tracking-widest text-[#6F6A60]">{label}</div>
    <div className="font-serif text-2xl mt-2 text-[#45423C] tabular">{value}</div>
    {sub && <div className={`text-xs mt-1 tabular ${cls}`}>{sub}</div>}
  </div>
);

const ROWS = [
  ["revenue", "Έσοδα", fmtEuro], ["revenue_budget", "Έσοδα Π/Υ", fmtEuro],
  ["occupancy_pct", "Πληρότητα", pct], ["adr", "Μέση τιμή (ADR)", fmtEuro], ["revpar", "RevPAR", fmtEuro],
  ["fnb_revenue", "Έσοδα F&B", fmtEuro], ["fnb_cost", "Αγορές F&B", fmtEuro], ["fnb_cost_pct", "Κόστος F&B %", pct],
  ["payroll", "Κόστος προσωπικού", fmtEuro], ["payroll_budget", "Προσωπικό Π/Υ", fmtEuro], ["payroll_pct", "Προσωπικό % εσόδων", pct],
  ["ebitda", "EBITDA", fmtEuro], ["ebitda_budget", "EBITDA Π/Υ", fmtEuro],
];

export const KpiTab = ({ year }) => {
  const { data, isLoading } = useQuery({ queryKey: ["erp-kpis", year], queryFn: async () => (await api.get(`/erp/kpis?year=${year}`)).data });
  if (isLoading || !data) return <div className="text-sm text-[#6F6A60]" data-testid="bs-kpi-loading">Φόρτωση…</div>;
  const y = data.ytd;
  const chart = data.months.map((m) => ({ name: MONTHS[m.month - 1], "Έσοδα": m.revenue, "Έσοδα Π/Υ": m.revenue_budget, EBITDA: m.ebitda, "EBITDA Π/Υ": m.ebitda_budget }));
  return (
    <div className="space-y-6" data-testid="bs-kpi-tab">
      <p className="text-xs text-[#6F6A60]">Από αρχή έτους = κλειστοί μήνες ({data.last_closed_month ? `Ιαν–${MONTHS[data.last_closed_month - 1]}` : "κανένας"}) · {data.units} βίλες.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card id="revenue" label="Έσοδα (από αρχή έτους)" value={fmtEuro(y.revenue)} sub={`Π/Υ ${fmtEuro(y.revenue_budget)} · ${fmtEuro(y.revenue - y.revenue_budget)}`} cls={diffCls(y.revenue, y.revenue_budget)} />
        <Card id="ebitda" label="EBITDA (από αρχή έτους)" value={fmtEuro(y.ebitda)} sub={`Π/Υ ${fmtEuro(y.ebitda_budget)} · ${fmtEuro(y.ebitda - y.ebitda_budget)}`} cls={diffCls(y.ebitda, y.ebitda_budget)} />
        <Card id="occupancy" label="Πληρότητα · ADR · RevPAR" value={pct(y.occupancy_pct)} sub={`ADR ${fmtEuro(y.adr)} · RevPAR ${fmtEuro(y.revpar)}`} />
        <Card id="costs" label="Κόστος F&B % · Προσωπικό %" value={`${pct(y.fnb_cost_pct)} · ${pct(y.payroll_pct)}`} sub={`Αγορές F&B ${fmtEuro(y.fnb_cost)} · Προσωπικό ${fmtEuro(y.payroll)}`} />
      </div>
      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid="bs-kpi-chart">
        <h2 className="font-medium text-[#45423C] mb-3">Έσοδα & EBITDA ανά μήνα — πραγματικά vs προϋπολογισμός</h2>
        <div className="h-72">
          <ResponsiveContainer>
            <ComposedChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE9E0" />
              <XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v) => fmtEuro(v)} /><Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Έσοδα Π/Υ" fill="#B9B2A5" /><Bar dataKey="Έσοδα" fill="#6E7A63" />
              <Line dataKey="EBITDA" stroke="#C86235" strokeWidth={2} /><Line dataKey="EBITDA Π/Υ" stroke="#45423C" strokeDasharray="4 4" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-x-auto" data-testid="bs-kpi-table">
        <table className="w-full text-xs">
          <thead className="bg-[#F3F0EA] text-[10px] uppercase tracking-wider text-[#6F6A60]">
            <tr><th className="px-3 py-2 text-left">KPI</th>{MONTHS.map((m) => <th key={m} className="px-2 py-2 text-right">{m}</th>)}<th className="px-2 text-right">Από αρχή έτους</th><th className="px-2 text-right">Έτος</th></tr>
          </thead>
          <tbody>
            {ROWS.map(([k, label, f]) => (
              <tr key={k} className="border-t border-[#E3DED4]" data-testid={`bs-kpi-row-${k}`}>
                <td className="px-3 py-1.5 font-medium text-[#45423C] whitespace-nowrap">{label}</td>
                {data.months.map((m) => <td key={m.month} className="px-2 py-1.5 text-right tabular whitespace-nowrap">{f(m[k])}</td>)}
                <td className="px-2 text-right tabular font-semibold whitespace-nowrap">{f(y[k])}</td>
                <td className="px-2 text-right tabular font-semibold whitespace-nowrap">{f(data.full_year[k])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#6F6A60]">Έσοδα: παραστατικά εσόδων (όπως ο Προϋπολογισμός Εσόδων). Αγορές F&B: κατηγορία «F&B Products» από SoftOne. EBITDA: όπως στα Αποτελέσματα P&L. Π/Υ EBITDA = Π/Υ εσόδων − Π/Υ μισθοδοσίας (με ΙΚΑ) − Π/Υ εξόδων.</p>
    </div>
  );
};
